import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Camera, Upload, X, Loader2, Sparkles, ShieldAlert, ShieldCheck,
  AlertTriangle, FileText, Copy, RotateCcw, ClipboardList, ImageIcon,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Section, Eyebrow, H1, Lead, FadeIn } from "../components/Primitives";
import { addHistoryItem } from "../lib/localdb";
import HistorySidebar from "../components/HistorySidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fileToDataUrl(f) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(f);
  });
}

function lineSplit(text) {
  return text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export default function CheckPage() {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const [mode, setMode] = useState("photo"); // "photo" | "text"
  const [preview, setPreview] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("");
  const [lines, setLines] = useState([]); // array of strings (original)
  const [issues, setIssues] = useState([]); // verify issues
  const [summary, setSummary] = useState("");
  const [historyTick, setHistoryTick] = useState(0);

  const reset = () => {
    setPreview(null);
    setText("");
    setLines([]);
    setIssues([]);
    setSummary("");
    setLoading(false);
    setStep("");
  };

  const verifyLines = async (linesArr, registerType = "Document") => {
    setStep("Checking for errors…");
    const { data } = await axios.post(`${API}/verify`, {
      register_type: registerType,
      columns: ["Sr", "Text"],
      rows: linesArr.map((t, i) => [String(i + 1), t]),
    }, { timeout: 90_000, withCredentials: true });
    const newIssues = Array.isArray(data?.issues) ? data.issues : [];
    const newSummary = data?.summary || "";
    setIssues(newIssues);
    setSummary(newSummary);
    // Zero-Server: save to encrypted local DB.
    try {
      const title = newIssues.length > 0
        ? `${newIssues.length} error${newIssues.length === 1 ? "" : "s"} · ${(linesArr[0] || "").slice(0, 60)}`
        : `Clean · ${(linesArr[0] || "").slice(0, 60)}`;
      await addHistoryItem({
        kind: "errorscan",
        title,
        payload: { lines: linesArr, issues: newIssues, summary: newSummary },
      });
      setHistoryTick((t) => t + 1);
    } catch (e) {
      console.warn("Could not save to local history:", e);
    }
  };

  const runFromPhoto = async (file) => {
    if (!file) return;
    setLoading(true);
    setIssues([]);
    setSummary("");
    setLines([]);
    try {
      const dataUrl = await fileToDataUrl(file);
      setPreview(dataUrl);
      const b64 = String(dataUrl).split(",")[1];

      setStep("Reading text from image…");
      const ocr = await axios.post(`${API}/digitize`, {
        image_base64: b64,
        mime_type: file.type || "image/jpeg",
      }, { timeout: 120_000, withCredentials: true });

      const cols = ocr.data?.columns || [];
      const rows = ocr.data?.rows || [];
      const register_type = ocr.data?.register_type || "Document";
      // The verbatim digitize endpoint now returns either:
      //   • a real table with multiple columns (registers), or
      //   • columns=['Text'] (single column) for free-form docs / notebooks / homework.
      let lineArr = [];
      if (cols.length === 1 && /text/i.test(cols[0])) {
        lineArr = rows.map((r) => String(r[0] ?? "").trim()).filter(Boolean);
      } else if (cols.length === 2 && /sr/i.test(cols[0]) && /text/i.test(cols[1])) {
        // Legacy fallback if a deployment still returns the old 2-column shape
        lineArr = rows.map((r) => String(r[1] ?? "").trim()).filter(Boolean);
      } else {
        lineArr = rows.map((r) =>
          r.map((c) => (c ?? "").toString()).filter(Boolean).join(" · "),
        ).filter(Boolean);
      }
      if (lineArr.length === 0) {
        toast.error("No readable text in that image. Try a clearer photo.");
        return;
      }
      setLines(lineArr);
      await verifyLines(lineArr, register_type);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Could not process the image.";
      console.error("CheckPage photo flow error:", err);
      toast.error(msg);
    } finally {
      setLoading(false);
      setStep("");
    }
  };

  const runFromText = async () => {
    const lineArr = lineSplit(text);
    if (lineArr.length === 0) {
      toast.error("Please paste or type some text first.");
      return;
    }
    setLoading(true);
    setIssues([]);
    setSummary("");
    setLines(lineArr);
    try {
      await verifyLines(lineArr, "Pasted text");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Could not verify the text.";
      console.error("CheckPage text flow error:", err);
      toast.error(msg);
    } finally {
      setLoading(false);
      setStep("");
    }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) runFromPhoto(f);
  };

  const copyCorrected = async () => {
    if (lines.length === 0) return;
    const issueByLine = new Map();
    for (const it of issues) {
      if (it.suggested) issueByLine.set(it.row, it.suggested);
    }
    const corrected = lines
      .map((ln, i) => issueByLine.get(i) || ln)
      .join("\n");
    try {
      await navigator.clipboard.writeText(corrected);
      toast.success("Corrected text copied");
    } catch {
      toast.error("Couldn't copy — please select and copy manually.");
    }
  };

  // Group issues by line index for inline rendering
  const issuesByLine = new Map();
  for (const it of issues) {
    if (!issuesByLine.has(it.row)) issuesByLine.set(it.row, []);
    issuesByLine.get(it.row).push(it);
  }

  const typeBadge = (t) =>
    t === "math"
      ? "bg-rose-50 text-rose-700 ring-rose-200"
      : t === "grammar"
      ? "bg-amber-50 text-amber-800 ring-amber-200"
      : "bg-orange-50 text-orange-800 ring-orange-200";

  return (
    <div data-testid="page-check">
      <Section className="pt-24 pb-10">
        <FadeIn>
          <Eyebrow>Error Scan · Beta</Eyebrow>
        </FadeIn>
        <FadeIn delay={0.05}>
          <H1 className="mt-6 max-w-3xl">
            Spot mistakes in any document.<br />Math, Science, Hindi & English.
          </H1>
        </FadeIn>
        <FadeIn delay={0.1}>
          <Lead className="mt-6 max-w-2xl">
            Upload a photo of a notebook page, register or worksheet — or just paste text.
            Errors are highlighted in <span className="text-red-700 font-medium">red</span>
            {" "}with the correction shown in <span className="text-emerald-700 font-medium">green</span>{" "}
            directly below.
          </Lead>
        </FadeIn>
      </Section>

      <Section className="pt-0">
        <div className="flex gap-6">
          <HistorySidebar
            kind="errorscan"
            refreshKey={historyTick}
            onLoad={(payload) => {
              if (!payload) return;
              setMode("text");
              setLines(payload.lines || []);
              setIssues(payload.issues || []);
              setSummary(payload.summary || "");
              setPreview(null);
            }}
          />
          <div className="min-w-0 flex-1">
        <div className="rounded-3xl border border-zinc-200 bg-white p-6 sm:p-8">
          {/* Mode tabs */}
          <div className="mb-5 inline-flex rounded-full bg-zinc-100 p-1 text-sm">
            <button
              type="button"
              data-testid="check-mode-photo"
              onClick={() => setMode("photo")}
              className={
                "rounded-full px-4 py-1.5 font-medium transition " +
                (mode === "photo" ? "bg-zinc-950 text-white" : "text-zinc-600 hover:text-zinc-900")
              }
            >
              <ImageIcon className="mr-1.5 inline h-4 w-4" /> Photo
            </button>
            <button
              type="button"
              data-testid="check-mode-text"
              onClick={() => setMode("text")}
              className={
                "rounded-full px-4 py-1.5 font-medium transition " +
                (mode === "text" ? "bg-zinc-950 text-white" : "text-zinc-600 hover:text-zinc-900")
              }
            >
              <ClipboardList className="mr-1.5 inline h-4 w-4" /> Paste text
            </button>
          </div>

          {mode === "photo" ? (
            <div className="grid gap-6 lg:grid-cols-12 lg:items-start">
              <div className="lg:col-span-7">
                <div className="text-xs font-medium uppercase tracking-wider text-zinc-500">
                  Upload or capture
                </div>
                <h2 className="mt-2 font-poppins text-xl font-semibold text-zinc-950">
                  Snap or pick a page
                </h2>
                <p className="mt-2 text-sm text-zinc-600">
                  Works for student notebooks, attendance / stock registers, prescriptions, notices and homework.
                  Hindi (Devanagari) + English supported.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button
                    data-testid="check-camera"
                    disabled={loading}
                    onClick={() => cameraRef.current?.click()}
                    className="rounded-full bg-zinc-950 px-5 text-white hover:bg-zinc-800"
                  >
                    <Camera className="mr-1.5 h-4 w-4" /> Camera
                  </Button>
                  <Button
                    data-testid="check-upload"
                    disabled={loading}
                    onClick={() => galleryRef.current?.click()}
                    variant="outline"
                    className="rounded-full border-zinc-300 px-5"
                  >
                    <Upload className="mr-1.5 h-4 w-4" /> Upload image
                  </Button>
                </div>
                <input
                  ref={cameraRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  data-testid="check-camera-input"
                  onChange={onPickFile}
                />
                <input
                  ref={galleryRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="check-file-input"
                  onChange={onPickFile}
                />
              </div>

              <div className="lg:col-span-5">
                {preview ? (
                  <div className="overflow-hidden rounded-2xl border border-zinc-200">
                    <img src={preview} alt="page preview" className="block w-full" />
                  </div>
                ) : (
                  <div className="grid place-items-center rounded-2xl border border-dashed border-zinc-300 bg-zinc-50 py-12 text-center text-sm text-zinc-500">
                    <FileText className="mb-2 h-6 w-6 text-zinc-400" />
                    Photo will preview here
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="grid gap-4">
              <div className="text-xs font-medium uppercase tracking-wider text-zinc-500">
                Paste any text — one statement per line
              </div>
              <textarea
                data-testid="check-text-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                disabled={loading}
                rows={8}
                placeholder={"Example:\n12 + 7 = 21\nThe Sun rises in the West.\nमैं स्कूल जाता हुं।\nIndia got independence in 1950."}
                className="w-full resize-y rounded-2xl border border-zinc-300 bg-white p-4 font-mono text-sm text-zinc-900 outline-none ring-zinc-400 placeholder:text-zinc-400 focus:ring-2"
                style={{ fontFamily: '"Noto Sans Devanagari", system-ui, sans-serif' }}
              />
              <div className="flex flex-wrap items-center gap-3">
                <Button
                  data-testid="check-text-submit"
                  disabled={loading || !text.trim()}
                  onClick={runFromText}
                  className="rounded-full bg-zinc-950 px-5 text-white hover:bg-zinc-800"
                >
                  <Sparkles className="mr-1.5 h-4 w-4" /> Check now
                </Button>
                <button
                  type="button"
                  data-testid="check-text-clear"
                  onClick={() => setText("")}
                  disabled={loading}
                  className="text-xs font-medium text-zinc-500 hover:text-zinc-900"
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          {loading && (
            <div
              data-testid="check-loading"
              className="mt-6 flex items-center gap-3 rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-700"
            >
              <Loader2 className="h-4 w-4 animate-spin" />
              {step || "Working…"}
            </div>
          )}
        </div>

        {/* Results */}
        {!loading && lines.length > 0 && (
          <div className="mt-8">
            {/* Summary header */}
            {issues.length === 0 ? (
              <div
                data-testid="check-clean"
                className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-5"
              >
                <ShieldCheck className="mt-0.5 h-5 w-5 text-emerald-700" />
                <div>
                  <div className="font-poppins text-sm font-semibold text-emerald-900">Looks clean</div>
                  <p className="mt-0.5 text-sm text-emerald-800/80">
                    {summary || "No errors detected. Nothing to correct."}
                  </p>
                </div>
              </div>
            ) : (
              <div
                data-testid="check-issues-summary"
                className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-5"
              >
                <ShieldAlert className="mt-0.5 h-5 w-5 text-red-700" />
                <div className="flex-1">
                  <div className="font-poppins text-sm font-semibold text-red-900">
                    {issues.length} potential error{issues.length === 1 ? "" : "s"} found
                  </div>
                  <p className="mt-0.5 text-sm text-red-800/80">{summary}</p>
                </div>
                <button
                  type="button"
                  data-testid="check-copy-corrected"
                  onClick={copyCorrected}
                  className="inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-white px-3 py-1.5 text-xs font-medium text-red-900 hover:bg-red-50"
                >
                  <Copy className="h-3.5 w-3.5" /> Copy corrected
                </button>
              </div>
            )}

            {/* Line-by-line view */}
            <ol
              data-testid="check-lines"
              className="mt-5 overflow-hidden rounded-2xl border border-zinc-200 bg-white"
              style={{ fontFamily: '"Noto Sans Devanagari", system-ui, sans-serif' }}
            >
              {lines.map((ln, i) => {
                const lineIssues = issuesByLine.get(i) || [];
                const hasIssue = lineIssues.length > 0;
                return (
                  <li
                    key={`${i}-${ln.slice(0, 20)}`}
                    data-testid={hasIssue ? `check-line-${i}-err` : `check-line-${i}-ok`}
                    className={
                      "flex gap-4 border-b border-zinc-100 px-5 py-4 last:border-b-0 " +
                      (hasIssue ? "bg-red-50/40" : "")
                    }
                  >
                    <span className="mt-0.5 w-6 shrink-0 text-right text-xs text-zinc-400">{i + 1}</span>
                    <div className="flex-1">
                      {hasIssue ? (
                        <>
                          <div className="text-sm text-red-700 line-through decoration-red-400/70 decoration-2">
                            {ln}
                          </div>
                          {lineIssues.map((it, idx) => (
                            <div key={idx} className="mt-1.5">
                              {it.suggested ? (
                                <div className="text-sm font-semibold text-emerald-700">
                                  ✓ {it.suggested}
                                </div>
                              ) : null}
                              <div className="mt-0.5 flex flex-wrap items-center gap-2">
                                <span
                                  className={
                                    "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset " +
                                    typeBadge(it.type)
                                  }
                                >
                                  <AlertTriangle className="h-3 w-3" /> {it.type}
                                </span>
                                <span className="text-xs italic text-zinc-600">
                                  {it.explanation}
                                </span>
                              </div>
                            </div>
                          ))}
                        </>
                      ) : (
                        <div className="text-sm text-zinc-800">{ln}</div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>

            <div className="mt-6 flex justify-center">
              <Button
                onClick={reset}
                variant="outline"
                data-testid="check-reset"
                className="rounded-full border-zinc-300 px-5"
              >
                <RotateCcw className="mr-1.5 h-4 w-4" /> Check another
              </Button>
            </div>
          </div>
        )}
          </div>
        </div>
      </Section>
    </div>
  );
}
